import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormGroup,
    FormGroupDirective,
    NgForm,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@ngneat/transloco';
import { environment } from 'environments/environment';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';

import { EstablishmentService } from 'app/modules/features/services/establishment.service';
import { RegexEnum } from 'app/modules/shared/enums/regexEnum';
import { ObjectRef } from 'app/modules/shared/models/objectRef';
import { PlanUtil } from 'app/modules/shared/utils/plan.util';

export interface AddEstablishmentDialogData {
    /** Whether the submit action is disabled (e.g. plan does not allow) */
    disabled?: boolean;
    /** Full list of available languages (ref list) — ObjectRef with { code, nativeLabel } */
    availableLanguages: ObjectRef[];
    /** Language codes pre-selected from the partner's current establishment (may be empty) */
    preselectedLanguageCodes: string[];
    /** Partner plan code — used to enforce the client-side language limit */
    planCode: string;
    /** Whether the partner has the unlimited-languages override from backoffice */
    hasUnlimitedLanguages: boolean;
    /** Full list of establishment types (ref list) — { code, label } */
    establishmentTypeList: ObjectRef[];
    /** Full list of capacity options (ref list) — { code, label } */
    capacityRefList: ObjectRef[];
    /** Partner id used by the slug-availability async validator */
    partnerId: number;
}

/**
 * Shows errors as soon as the control is dirty (user has typed) — without waiting
 * for a blur event. Used on the slug input so the async "already taken" error
 * surfaces immediately instead of only on focus-out.
 */
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (!control) {
            return false;
        }
        const submitted = !!(form && form.submitted);
        return !!(control.invalid && (control.dirty || control.touched || submitted));
    }
}

export interface AddEstablishmentDialogResult {
    name: string;
    slug: string;
    establishmentTypeCode: string;
    capacityCode: string;
    languageCodes: string[];
}

/**
 * Quick-create dialog used from the establishment switcher.
 *
 * Collects the essentials in a single step (name, public-URL slug, establishment type,
 * capacity, languages) so the partner lands directly on /app/menus after creation
 * instead of being routed through the onboarding wizard. Slug, type and capacity
 * reuse the same validators and async slug-availability check as the wizard step 1.
 */
@Component({
    selector: 'add-establishment-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        TranslocoModule,
    ],
    templateUrl: './add-establishment-dialog.component.html',
})
export class AddEstablishmentDialogComponent implements OnInit, OnDestroy {
    form: FormGroup;
    submitting = false;
    /** Available languages shown in the multi-select, sorted with priority codes first */
    readonly availableLanguages: ObjectRef[];
    /** Max selectable languages for the partner's plan (Infinity when unlimited) */
    readonly languageLimit: number;
    /** Priority language codes shown first in the dropdown (matches wizard behaviour) */
    readonly PRIORITY_LANGUAGE_CODES = ['EN', 'ES', 'FR', 'IT'];
    /** Base URL used to preview the public menu link alongside the slug input */
    readonly menuBaseUrl: string = environment.menuUrl;
    /** Shared matcher that shows validation errors on dirty (no blur required) */
    readonly immediateErrorMatcher = new ImmediateErrorStateMatcher();

    /** True once the user has manually edited the slug — disables the auto-slug sync */
    isSlugManuallyEdited = false;
    /** Last value written by the auto-slug mechanism, used to detect manual edits */
    private lastAutoSlug = '';
    /** Guard to avoid recursive valueChanges loops when we programmatically update the slug */
    private isProgrammaticSlugUpdate = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _fb: FormBuilder,
        private _establishmentService: EstablishmentService,
        private _dialogRef: MatDialogRef<AddEstablishmentDialogComponent, AddEstablishmentDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: AddEstablishmentDialogData,
    ) {
        this.availableLanguages = this._sortLanguages(data.availableLanguages || []);
        this.languageLimit = data.hasUnlimitedLanguages
            ? Infinity
            : PlanUtil.getLanguageLimit(data.planCode);

        // Pre-select languages from the partner's current establishment, clamped to
        // the plan limit so the defaults never exceed what the partner is allowed to save.
        const preselected = (data.preselectedLanguageCodes || []).filter(Boolean);
        const initialCodes = this.languageLimit === Infinity
            ? preselected
            : preselected.slice(0, this.languageLimit);

        this.form = this._fb.group({
            name: ['', [Validators.required, Validators.maxLength(120)]],
            slug: [
                '',
                [Validators.required, Validators.minLength(3), Validators.maxLength(60), Validators.pattern(RegexEnum.SLUG)],
                [this._slugAvailabilityValidator()],
            ],
            establishmentTypeCode: ['', [Validators.required]],
            capacityCode: ['', [Validators.required]],
            languageCodes: [initialCodes, [Validators.required, Validators.minLength(1)]],
        });
    }

    ngOnInit(): void {
        // Auto-slug: while the user types a name and has not manually edited the slug,
        // regenerate a clean slug from the name on the fly.
        this.form.get('name').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((name: string) => {
                // If the name is cleared, allow auto-slug to resume on next character.
                if (!name || name.trim() === '') {
                    this.isSlugManuallyEdited = false;
                    return;
                }
                if (this.isSlugManuallyEdited) {
                    return;
                }
                const auto = this._slugify(name);
                this.lastAutoSlug = auto;
                this.isProgrammaticSlugUpdate = true;
                const slugControl = this.form.get('slug');
                slugControl.setValue(auto);
                // Mark as dirty + touched so async validation errors render immediately
                // without requiring the user to focus and blur the slug field.
                slugControl.markAsDirty();
                slugControl.markAsTouched();
                this.isProgrammaticSlugUpdate = false;
            });

        // Detect manual edits on the slug input (and sanitize as the user types so the
        // field only ever contains valid characters).
        this.form.get('slug').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((val: string) => {
                if (this.isProgrammaticSlugUpdate) {
                    return;
                }
                const sanitized = this._slugifyWhileTyping(val || '');
                if (sanitized !== val) {
                    this.isProgrammaticSlugUpdate = true;
                    this.form.get('slug').setValue(sanitized);
                    this.isProgrammaticSlugUpdate = false;
                }
                if (sanitized !== this.lastAutoSlug) {
                    this.isSlugManuallyEdited = true;
                }
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /** True when the selection has already reached the plan limit (used to disable unselected options) */
    get hasReachedLimit(): boolean {
        if (this.languageLimit === Infinity) {
            return false;
        }
        const selected = (this.form.get('languageCodes').value || []) as string[];
        return selected.length >= this.languageLimit;
    }

    /** Whether the given option should be disabled (reached limit and not already selected) */
    isOptionDisabled(code: string): boolean {
        if (!this.hasReachedLimit) {
            return false;
        }
        const selected = (this.form.get('languageCodes').value || []) as string[];
        return !selected.includes(code);
    }

    priorityLanguages(): ObjectRef[] {
        return this.availableLanguages.filter(l => this.PRIORITY_LANGUAGE_CODES.includes(l.code));
    }

    otherLanguages(): ObjectRef[] {
        return this.availableLanguages.filter(l => !this.PRIORITY_LANGUAGE_CODES.includes(l.code));
    }

    /** Full public menu URL preview shown under the slug input */
    get fullSlugUrl(): string {
        const slug = this.form?.get('slug')?.value || '';
        return slug ? `${this.menuBaseUrl}${slug}` : '';
    }

    /** True when the async slug-availability validator is currently running */
    get isSlugChecking(): boolean {
        return this.form?.get('slug')?.pending === true;
    }

    cancel(): void {
        this._dialogRef.close();
    }

    submit(): void {
        if (this.form.invalid || this.submitting) {
            this.form.markAllAsTouched();
            return;
        }
        const { name, slug, establishmentTypeCode, capacityCode, languageCodes } = this.form.value;
        const selectedCodes = ((languageCodes || []) as string[]).filter(Boolean);
        if (selectedCodes.length === 0) {
            this.form.get('languageCodes').setErrors({ required: true });
            return;
        }
        const result: AddEstablishmentDialogResult = {
            name: (name || '').trim(),
            slug: (slug || '').trim(),
            establishmentTypeCode,
            capacityCode,
            languageCodes: selectedCodes,
        };
        this._dialogRef.close(result);
    }

    /** Priority codes first (EN/ES/FR/IT), then the rest sorted by native label. */
    private _sortLanguages(langs: ObjectRef[]): ObjectRef[] {
        const byNativeLabel = (a: ObjectRef, b: ObjectRef) =>
            ((a as any).nativeLabel || a.code).localeCompare((b as any).nativeLabel || b.code);
        const priority = langs
            .filter(l => this.PRIORITY_LANGUAGE_CODES.includes(l.code))
            .sort(byNativeLabel);
        const others = langs
            .filter(l => !this.PRIORITY_LANGUAGE_CODES.includes(l.code))
            .sort(byNativeLabel);
        return [...priority, ...others];
    }

    /** Create a URL-friendly slug from an arbitrary label — same rules as the wizard. */
    private _slugify(value: string): string {
        if (!value) { return ''; }
        return value
            .toString()
            .normalize('NFD')
            .replace(/\p{Diacritic}+/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/--+/g, '-')
            .substring(0, 60);
    }

    /**
     * Sanitize slug while the user is typing — preserves leading/trailing hyphens so
     * what the user types stays visible, but restricts the character set.
     */
    private _slugifyWhileTyping(value: string): string {
        if (!value) { return ''; }
        return value
            .toString()
            .normalize('NFD')
            .replace(/\p{Diacritic}+/gu, '')
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/--+/g, '-')
            .substring(0, 60);
    }

    /** Async validator — debounced slug availability check against the partner's backend. */
    private _slugAvailabilityValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            const slug = (control.value || '').trim();
            if (!slug) {
                return of(null);
            }
            if (control.hasError && control.hasError('pattern')) {
                return of(null);
            }
            const partnerId = this.data?.partnerId;
            if (!partnerId) {
                return of(null);
            }
            return of(slug).pipe(
                debounceTime(400),
                distinctUntilChanged(),
                switchMap((s) => this._establishmentService.isSlugAvailable(partnerId, s)),
                map((isAvailable) => (isAvailable ? null : { slugTaken: true })),
                catchError(() => of({ slugTaken: true })),
            );
        };
    }
}
